import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { siteConfig } from "../config/site";
import { findPublishedProduct, isVerifiedInStock, type Product } from "../data/catalogue";

export interface CartLine {
  productId: string;
  quantity: number;
}

interface CartContextValue {
  lines: CartLine[];
  items: Array<{ product: Product; quantity: number }>;
  itemCount: number;
  subtotalPence: number | null;
  add: (productId: string) => { ok: boolean; reason?: string };
  setQuantity: (productId: string, quantity: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
  canCheckout: boolean;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>([]);

  const value = useMemo<CartContextValue>(() => {
    const items = lines
      .map((line) => {
        const product = findPublishedProduct(line.productId);
        return product ? { product, quantity: line.quantity } : null;
      })
      .filter((item): item is { product: Product; quantity: number } => item !== null);

    const priced = items.every((item) => item.product.retailPricePence !== null);
    const subtotalPence = priced
      ? items.reduce((sum, item) => sum + (item.product.retailPricePence ?? 0) * item.quantity, 0)
      : null;

    return {
      lines,
      items,
      itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
      subtotalPence,
      add(productId) {
        if (!siteConfig.commerceEnabled) {
          return { ok: false, reason: "Orders are not currently available." };
        }
        const product = findPublishedProduct(productId);
        if (!product || !isVerifiedInStock(product)) {
          return { ok: false, reason: "This product is not available to order." };
        }
        setLines((current) => {
          const existing = current.find((line) => line.productId === productId);
          if (existing) {
            return current.map((line) =>
              line.productId === productId ? { ...line, quantity: line.quantity + 1 } : line
            );
          }
          return [...current, { productId, quantity: 1 }];
        });
        return { ok: true };
      },
      setQuantity(productId, quantity) {
        setLines((current) =>
          quantity < 1
            ? current.filter((line) => line.productId !== productId)
            : current.map((line) =>
                line.productId === productId ? { ...line, quantity } : line
              )
        );
      },
      remove(productId) {
        setLines((current) => current.filter((line) => line.productId !== productId));
      },
      clear() {
        setLines([]);
      },
      canCheckout: false
    };
  }, [lines]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
}
